import { toast } from 'react-toastify';
import api from '../config/api';

const PENDING_ACTION_KEY = 'pendingActionAfterLogin';

/**
 * Save a pending action (add to cart / add to wishlist) to run after login/signup.
 * @param {{ type: 'addToCart' | 'addToWishlist' | 'addComboToCart', productId?: number, comboId?: number, variantId?: number, quantity?: number, comboSelections?: object }} action
 */
export function savePendingAction(action) {
  try {
    sessionStorage.setItem(PENDING_ACTION_KEY, JSON.stringify(action));
  } catch (e) {
    console.warn('Could not save pending action', e);
  }
}

/**
 * Run pending action after login/signup: add to cart or wishlist, then navigate.
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @returns {Promise<boolean>} true if a pending action was completed (and navigation happened), false otherwise
 */
export async function completePendingAction(navigate) {
  try {
    const raw = sessionStorage.getItem(PENDING_ACTION_KEY);
    if (!raw) return false;

    const action = JSON.parse(raw);
    sessionStorage.removeItem(PENDING_ACTION_KEY);

    if (action.type === 'addToWishlist' && action.comboId) {
      await api.post('/wishlist', { combo_id: action.comboId });
      window.dispatchEvent(new Event('wishlistUpdated'));
      toast.success('Added to wishlist');
      navigate('/wishlist');
      return true;
    }

    if (action.type === 'addToWishlist' && action.productId) {
      await api.post('/wishlist', { product_id: action.productId });
      window.dispatchEvent(new Event('wishlistUpdated'));
      toast.success('Added to wishlist');
      navigate('/wishlist');
      return true;
    }

    if (action.type === 'addToCart' && action.productId) {
      if (action.variantId != null && action.quantity != null) {
        await api.post('/cart', {
          product_id: action.productId,
          variant_id: action.variantId,
          quantity: action.quantity,
        });
        window.dispatchEvent(new Event('cartUpdated'));
        toast.success('Added to cart');
        navigate('/cart');
        return true;
      }
      navigate(`/products/${action.productId}`);
      return true;
    }

    if (action.type === 'addComboToCart' && action.comboId) {
      await api.post('/cart', {
        combo_id: parseInt(action.comboId, 10),
        combo_selections: action.comboSelections,
        quantity: action.quantity || 1,
      });
      window.dispatchEvent(new Event('cartUpdated'));
      toast.success('Combo added to cart');
      navigate('/cart');
      return true;
    }

    return false;
  } catch (error) {
    console.error('Complete pending action error:', error);
    toast.error(error.response?.data?.error || 'Could not add item. Please try again.');
    return false;
  }
}
